import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { CheckCircle2, ShoppingBag, Plus, Minus, Trash2, Loader2, Heart } from 'lucide-react';
import { toast } from 'sonner';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from './ui/sheet';
import { Button } from './ui/button';
import CrossSellSection from './CrossSellSection';
import FreeShippingProgress from './FreeShippingProgress';
import PromoCodeField from './PromoCodeField';
import { CartPackUpsell } from './CartPackUpsell';
import CartDrawerWishlist from './CartDrawerWishlist';
import { useCartFit, CartFitBadge } from './CartFitBadge';
import { useMediaQuery } from '../hooks/useMediaQuery';
import { authAPI, cartAPI, cartLineKey } from '../services/api';
import { CART_DRAWER_EVENT } from '../services/cartDrawer';
import { fmtEuro, useShippingSummary } from '../services/shippingInfo';
import { computeSavings, promoSession } from '../services/savings';
import { usePromoRevalidation } from '../hooks/usePromoRevalidation';
import { wishlistStore } from '../services/wishlistStore';
import { useSelectedVehicle } from '../services/vehicleStore';
import { useT } from '../services/i18n';

const SKIP_PATHS = ['/panier', '/checkout'];

const CartDrawerLine = ({ item, onQty, onRemove, onSaveLater, busy, t, fit, vehicle, fuelTypes }) => {
  const step = item.pack_id ? 1 : (item.sale_multiple || 1);
  return (
    <li className="flex gap-3 py-3" data-testid={`cart-drawer-item-${item.product_id}`}>
      <div className="w-16 h-16 rounded-lg border border-gray-200 bg-white flex items-center justify-center overflow-hidden shrink-0">
        {item.image ? <img src={item.image} alt={item.name} className="w-full h-full object-contain" /> : <ShoppingBag size={20} className="text-gray-300" />}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-900 line-clamp-2" data-testid="cart-drawer-item-name">{item.name}</p>
        {item.pack_label && <p className="text-xs text-gray-500">{item.pack_label}</p>}
        <CartFitBadge status={fit} vehicle={vehicle} productId={item.product_id} fuelTypes={fuelTypes} />
        <div className="mt-2 flex items-center justify-between gap-2">
          <div className="inline-flex items-center border border-gray-200 rounded-md">
            <button type="button" aria-label="-" disabled={busy} onClick={() => onQty(item, -step)} className="p-1.5 text-gray-600 hover:bg-gray-100 disabled:opacity-40" data-testid="cart-drawer-qty-minus"><Minus size={14} /></button>
            <span className="w-7 text-center text-sm font-medium" data-testid="cart-drawer-qty">{item.quantity}</span>
            <button type="button" aria-label="+" disabled={busy} onClick={() => onQty(item, step)} className="p-1.5 text-gray-600 hover:bg-gray-100 disabled:opacity-40" data-testid="cart-drawer-qty-plus"><Plus size={14} /></button>
          </div>
          <span className="text-sm font-semibold text-gray-900" data-testid="cart-drawer-line-total">{fmtEuro(item.price * item.quantity)}</span>
          <button type="button" aria-label={t('cart.remove')} disabled={busy} onClick={() => onRemove(item)} className="p-1.5 text-gray-400 hover:text-red-600 disabled:opacity-40" data-testid="cart-drawer-remove"><Trash2 size={16} /></button>
        </div>
        <button type="button" disabled={busy} onClick={() => onSaveLater(item)} className="mt-1.5 inline-flex items-center gap-1 text-[11px] text-gray-500 hover:text-orange-600 disabled:opacity-40" data-testid="cart-drawer-save-later"><Heart size={11} /> {t('cartDrawer.saveLater')}</button>
      </div>
    </li>
  );
};

const CartDrawer = () => {
  const { t } = useT();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyKey, setBusyKey] = useState(null);
  const [lastAdded, setLastAdded] = useState(null);
  const [promo, setPromo] = useState(() => promoSession.get());
  const shipInfo = useShippingSummary();
  const vehicle = useSelectedVehicle();
  const cartFit = useCartFit(open ? items : [], vehicle);
  const isMobile = useMediaQuery('(max-width: 639px)');
  const [dragY, setDragY] = useState(0);
  const touchStart = useRef(null);
  const changePromo = (p) => { setPromo(p); promoSession.set(p); };
  usePromoRevalidation(open ? items : [], promo, setPromo);

  const load = useCallback(() => cartAPI.get().then(c => setItems(c.items || [])).catch(() => {}), []);

  useEffect(() => {
    window.dispatchEvent(new CustomEvent('cart-drawer-state', { detail: { open } }));
    if (!open) setDragY(0);
  }, [open]);

  const onTouchStart = (e) => { touchStart.current = e.touches[0].clientY; };
  const onTouchMove = (e) => { if (touchStart.current != null) setDragY(Math.max(0, e.touches[0].clientY - touchStart.current)); };
  const onTouchEnd = () => { if (dragY > 90) setOpen(false); else setDragY(0); touchStart.current = null; };

  useEffect(() => {
    const onOpen = (e) => {
      if (SKIP_PATHS.some(p => location.pathname.startsWith(p))) return;
      setLastAdded(e.detail?.productId || null);
      setPromo(promoSession.get());
      setLoading(true);
      load().finally(() => setLoading(false));
      setOpen(true);
    };
    window.addEventListener(CART_DRAWER_EVENT, onOpen);
    return () => window.removeEventListener(CART_DRAWER_EVENT, onOpen);
  }, [location.pathname, load]);

  useEffect(() => {
    if (!open) return;
    window.addEventListener('cart-updated', load);
    return () => window.removeEventListener('cart-updated', load);
  }, [open, load]);

  const onQty = async (item, delta) => {
    const next = item.quantity + delta;
    setBusyKey(cartLineKey(item));
    try {
      if (next <= 0) await cartAPI.removeItem(item.product_id, item.pack_id || null);
      else await cartAPI.updateItem(item.product_id, next, item.pack_id || null);
      await load();
    } catch (e) { /* toast shown by cartAPI */ } finally { setBusyKey(null); }
  };

  const onRemove = async (item) => {
    setBusyKey(cartLineKey(item));
    try { await cartAPI.removeItem(item.product_id, item.pack_id || null); await load(); } finally { setBusyKey(null); }
  };

  const onSaveLater = async (item) => {
    setBusyKey(cartLineKey(item));
    try {
      if (!wishlistStore.has(item.product_id)) await wishlistStore.toggle(item.product_id);
      await cartAPI.removeItem(item.product_id, item.pack_id || null);
      await load();
      toast.success(t('wishlist.added'));
    } catch (e) { toast.error(e.response?.data?.detail || e.message); } finally { setBusyKey(null); }
  };

  const go = (path) => { setOpen(false); navigate(path); };
  const count = items.reduce((n, i) => n + i.quantity, 0);
  const subtotal = items.reduce((s, i) => s + i.price * i.quantity, 0);
  const promoDiscount = computeSavings(items, promo, shipInfo).promoDiscount;
  const isAuth = authAPI.isAuthenticated();
  const cartIds = items.map(i => i.product_id);
  const sourceIds = lastAdded ? [lastAdded] : cartIds.slice(0, 3);

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent side={isMobile ? 'bottom' : 'right'} className={`p-0 flex flex-col ${isMobile ? 'h-[88vh] rounded-t-2xl' : 'w-full sm:max-w-md'}`} style={dragY ? { transform: `translateY(${dragY}px)`, transition: 'none' } : undefined} data-testid="cart-drawer" data-mobile={isMobile ? '1' : '0'}>
        {isMobile && (
          <div className="pt-2 pb-1 flex justify-center touch-none" onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd} data-testid="cart-drawer-handle">
            <span className="block w-12 h-1.5 rounded-full bg-gray-300" />
          </div>
        )}
        <SheetHeader className={`px-5 ${isMobile ? 'pt-1' : 'pt-5'} pb-3 border-b border-gray-100 bg-gradient-to-r ${lastAdded ? 'from-green-50' : 'from-orange-50'} to-white text-left`} onTouchStart={isMobile ? onTouchStart : undefined} onTouchMove={isMobile ? onTouchMove : undefined} onTouchEnd={isMobile ? onTouchEnd : undefined}>
          <SheetTitle className={`flex items-center gap-2 text-base ${lastAdded ? 'text-green-800' : 'text-gray-900'}`} data-testid="cart-drawer-title">{lastAdded ? <CheckCircle2 size={20} /> : <ShoppingBag size={20} />} {lastAdded ? t('product.added') : t('cartDrawer.title')}</SheetTitle>
          <SheetDescription className="text-gray-600 text-sm" data-testid="cart-drawer-count">{t('cart.title', { n: count, count })}</SheetDescription>
        </SheetHeader>
        <div className="flex-1 overflow-y-auto px-5">
          {loading && !items.length ? (
            <div className="flex justify-center py-10"><Loader2 className="animate-spin text-gray-400" /></div>
          ) : items.length ? (
            <ul className="divide-y divide-gray-100" data-testid="cart-drawer-items">
              {items.map(item => <CartDrawerLine key={cartLineKey(item)} item={item} onQty={onQty} onRemove={onRemove} onSaveLater={onSaveLater} busy={busyKey === cartLineKey(item)} t={t} fit={cartFit[item.product_id]} vehicle={vehicle} fuelTypes={cartFit.__meta?.fuel_types?.[item.product_id]} />)}
            </ul>
          ) : (
            <>
              <p className="text-sm text-gray-500 py-10 text-center" data-testid="cart-drawer-empty">{t('cart.empty')}</p>
              {open && <CartDrawerWishlist />}
            </>
          )}
          {open && items.length > 0 && <div className="pt-1"><CartPackUpsell items={items} vehicle={vehicle} onAdded={load} onPromo={changePromo} /></div>}
          {open && sourceIds.length > 0 && (
            <CrossSellSection placement="added" productIds={sourceIds} cartIds={cartIds} title={t('xsell.completeTitle')} subtitle={t('xsell.completeHint')} compact gridClass="grid gap-2 grid-cols-1" className="border-t border-gray-100 pt-4 pb-4" testId="cart-drawer-cross-sell" />
          )}
        </div>
        <div className="px-5 py-4 border-t border-gray-100 bg-gray-50 space-y-3">
          {items.length > 0 && !promo?.free_shipping && shipInfo.freeFrom != null && <FreeShippingProgress subtotal={subtotal} freeFrom={shipInfo.freeFrom} countryName={shipInfo.country !== 'FR' ? shipInfo.countryName : null} />}
          {items.length > 0 && (isAuth
            ? <div data-testid="cart-drawer-promo"><PromoCodeField promo={promo} onChange={changePromo} /></div>
            : <p className="text-xs text-gray-500" data-testid="cart-drawer-promo-login-hint">{t('cart.promoLogin')}</p>)}
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-600">{t('common.subtotal')}</span>
            <span className={`font-bold text-gray-900 ${promoDiscount > 0 ? 'text-sm' : 'text-lg'}`} data-testid="cart-drawer-subtotal">{fmtEuro(subtotal)}</span>
          </div>
          {promoDiscount > 0 && (
            <>
              <div className="flex items-center justify-between text-sm text-green-700" data-testid="cart-drawer-promo-line">
                <span>{t('common.discount', { code: promo.code })}</span>
                <span className="font-medium">-{fmtEuro(promoDiscount)}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-900 font-semibold">{t('common.total')}</span>
                <span className="text-lg font-bold text-gray-900" data-testid="cart-drawer-total">{fmtEuro(Math.max(subtotal - promoDiscount, 0))}</span>
              </div>
            </>
          )}
          <div className="grid grid-cols-2 gap-2">
            <Button variant="outline" onClick={() => go('/panier')} data-testid="cart-drawer-go-cart"><ShoppingBag size={16} className="mr-2" /> {t('cartDrawer.goCart')}</Button>
            <Button className="bg-orange-500 hover:bg-orange-600" disabled={!items.length} onClick={() => go(isAuth ? '/checkout' : '/connexion?redirect=/checkout')} data-testid="cart-drawer-checkout">{t('cartDrawer.checkout')}</Button>
          </div>
          <button type="button" onClick={() => setOpen(false)} className="w-full text-xs text-gray-500 hover:text-gray-800 underline" data-testid="cart-drawer-continue">{t('xsell.continue')}</button>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default CartDrawer;
